import { apiClient } from './apiClient';

export type DecisionEngineSettings = {
  baseUrl: string;
  apiKeyConfigured: boolean;
  hasSavedSettings: boolean;
  environmentAddressAvailable: boolean;
  defaultLanguage: string;
  checkpoint: string;
  timeoutSeconds: number;
  maxConcurrent: number;
  maxConcurrentPerCaller: number;
  circuitFailureThreshold: number;
  circuitBreakSeconds: number;
  enabled: boolean;
  customerSuccessEnabled: boolean;
  emailTriageEnabled: boolean;
  chatMessagesEnabled: boolean;
  technologyEnabled: boolean;
  monitoringEnabled: boolean;
};

export type DecisionEngineSettingsSave = {
  baseUrl: string;
  apiKey?: string;
  clearApiKey: boolean;
  defaultLanguage: string;
  checkpoint: string;
  timeoutSeconds: number;
  maxConcurrent: number;
  maxConcurrentPerCaller: number;
  circuitFailureThreshold: number;
  circuitBreakSeconds: number;
  enabled: boolean;
  customerSuccessEnabled: boolean;
  emailTriageEnabled: boolean;
  chatMessagesEnabled: boolean;
  technologyEnabled: boolean;
  monitoringEnabled: boolean;
};

export type DecisionEngineLocalStatus = {
  available: boolean;
  running: boolean;
  starting: boolean;
  address: string;
  message: string;
  lines: string[];
};

export type DecisionEngineTestRequest = {
  state: string;
  type: 'choice' | 'score' | 'noul';
  instructions: string;
  criteria?: Record<string, string>;
  levels?: string[];
};

export type DecisionEngineTestAnswer = {
  type: string;
  choice?: string | null;
  score?: number | null;
  noul?: number | null;
  probabilities: Record<string, number>;
  legend?: Record<string, string> | null;
  confidence?: number | null;
  answerConfidence?: number | null;
  lowConfidence?: boolean | null;
};

export type DecisionEngineTestResult = {
  succeeded: boolean;
  elapsedMilliseconds: number;
  message?: string | null;
  answer?: DecisionEngineTestAnswer | null;
};

export async function fetchDecisionEngineSettings(): Promise<DecisionEngineSettings> {
  const response = await apiClient.get<DecisionEngineSettings>('/v1/admin/decision-engine');
  return response.data;
}

export async function saveDecisionEngineSettings(
  payload: DecisionEngineSettingsSave,
): Promise<DecisionEngineSettings> {
  const response = await apiClient.put<DecisionEngineSettings>('/v1/admin/decision-engine', payload);
  return response.data;
}

export async function fetchDecisionEngineLocal(): Promise<DecisionEngineLocalStatus> {
  const response = await apiClient.get<DecisionEngineLocalStatus>('/v1/admin/decision-engine/local');
  return response.data;
}

export async function startDecisionEngineLocal(): Promise<DecisionEngineLocalStatus> {
  const response = await apiClient.post<DecisionEngineLocalStatus>('/v1/admin/decision-engine/local/start');
  return response.data;
}

export async function stopDecisionEngineLocal(): Promise<DecisionEngineLocalStatus> {
  const response = await apiClient.post<DecisionEngineLocalStatus>('/v1/admin/decision-engine/local/stop');
  return response.data;
}

export async function resetDecisionEngineLocal(): Promise<DecisionEngineLocalStatus> {
  const response = await apiClient.post<DecisionEngineLocalStatus>('/v1/admin/decision-engine/local/reset');
  return response.data;
}

export async function testDecisionEngine(
  payload: DecisionEngineTestRequest,
): Promise<DecisionEngineTestResult> {
  const response = await apiClient.post<DecisionEngineTestResult>(
    '/v1/admin/decision-engine/test',
    payload,
  );
  return response.data;
}
